import { useState } from 'react';
import { ReactStateUpdate } from 'src/helpers/TStypes';
import { getStateUpdate } from 'src/helpers/getStateUpdate';

export function useLocalStorage<T>(key: string, initialValue: any) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? initialValue(getItem(item), key) : getValue(initialValue);
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return getValue(initialValue);
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: ReactStateUpdate<T>) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = getStateUpdate({ state: storedValue, update: value})
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue, setValue] as const;
}

const getValue = value => (value instanceof Function ? value() : value);

const getItem = item => {
  try {
    return JSON.parse(item);
  } catch {
    return item;
  }
};
